import type { Database } from '../../lib/database.types';
import type { Prospect } from './DealCard';

export const formatMoney = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

/** Dollars, with cents only when there are any — for values sent to Google Ads, which keep cents. */
export const exactMoney = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value);

export const shortMoney = (value: number) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${Math.round(value / 1000)}K`;
  return `$${value}`;
};

export const daysInStage = (enteredAt: string | null | undefined) => {
  if (!enteredAt) return '—';
  const days = Math.max(0, Math.floor((Date.now() - new Date(enteredAt).getTime()) / 86400000));
  return days === 0 ? 'today' : `${days}d`;
};

/** Epoch ms of the card's stage entry, 0 when unset — for sorting newest-first. */
export const stageEnteredMs = (p: Pick<Prospect, 'stage_entered_at'>) =>
  p.stage_entered_at ? new Date(p.stage_entered_at).getTime() : 0;

type ClickIds = Pick<Prospect, 'gclid' | 'gbraid' | 'wbraid'>;

/**
 * Google Ads attribution is the presence of a Google click id on the row —
 * not the editable "ads" tag, which the site only sets at insert time and
 * which anyone can delete in the panel.
 */
export const adsClickKind = (p: ClickIds): 'gclid' | 'gbraid' | 'wbraid' | null =>
  p.gclid ? 'gclid' : p.gbraid ? 'gbraid' : p.wbraid ? 'wbraid' : null;

export const isAdsLead = (p: ClickIds) => adsClickKind(p) !== null;

export interface AdsUploadState {
  tone: 'up' | 'warn' | 'down';
  label: string;
  title: string;
}

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/** The uploader only considers rows created on/after this date (START_DATE in upload_conversions.py). */
const ADS_UPLOAD_START = new Date('2026-09-04T00:00:00');

/**
 * Project Signal upload status. The uploader takes every qualified / closed-won
 * card created since ADS_UPLOAD_START — matched by click id when there is one,
 * otherwise by hashed email — stamps ads_*_uploaded_at when the conversion
 * reaches Google Ads, and ads_upload_error when it gave up on the row. Null
 * until a moment has been stamped, so cards still in Leads show nothing.
 * Not gated on a click id: an email-matched upload is still an upload.
 */
export const adsUploadState = (p: Prospect): AdsUploadState | null => {
  if (!p.created_at || new Date(p.created_at) < ADS_UPLOAD_START) return null;
  if (p.ads_upload_error) {
    return { tone: 'down', label: 'Upload failed', title: `Google Ads upload failed: ${p.ads_upload_error}` };
  }
  if (p.closed_won_at && !p.ads_closed_uploaded_at) {
    return { tone: 'warn', label: 'Closed Sale pending', title: 'Closed Sale is waiting for the next Google Ads upload' };
  }
  if (p.qualified_at && !p.ads_qualified_uploaded_at) {
    return { tone: 'warn', label: 'Qualified Lead pending', title: 'Qualified Lead is waiting for the next Google Ads upload' };
  }
  if (p.ads_closed_uploaded_at) {
    return { tone: 'up', label: 'Closed Sale sent', title: `Closed Sale sent to Google Ads ${shortDate(p.ads_closed_uploaded_at)}` };
  }
  if (p.ads_qualified_uploaded_at) {
    return { tone: 'up', label: 'Qualified Lead sent', title: `Qualified Lead sent to Google Ads ${shortDate(p.ads_qualified_uploaded_at)}` };
  }
  return null;
};

/** One row of prospects_ads_stats. close_rate is what the uploader multiplies by: the override when set, else running. */
export type AdsStats = Database['public']['Views']['prospects_ads_stats']['Row'];

export const formatRate = (rate: number | null | undefined) =>
  rate == null ? '—' : `${(Number(rate) * 100).toFixed(1).replace(/\.0$/, '')}%`;

/** Qualified Lead value the uploader sends: deal_value × close_rate, cents rounded. */
export const qualifiedLeadValue = (dealValue: number, closeRate: number) =>
  Math.round(dealValue * closeRate * 100) / 100;

/** Closed Sale value the uploader sends: gross_profit when entered, else deal_value. */
export const closedSaleValue = (p: Pick<Prospect, 'gross_profit' | 'deal_value'>) => p.gross_profit ?? p.deal_value;

export type AdsLineState = 'sent' | 'queued' | 'later' | 'never';

export interface AdsLine {
  state: AdsLineState;
  /** "Sent Sep 16", "Next upload", "At Qualified", "Won't send". */
  status: string;
  value: number | null;
  /** How the value is made, e.g. "$9,600 deal × 100%". */
  basis: string;
}

export interface AdsReadout {
  /** Why nothing goes at all (predates the uploader). Lines are omitted when set. */
  blocked: string | null;
  error: string | null;
  match: string;
  matchTone: 'ok' | 'weak';
  qualified: AdsLine | null;
  closed: AdsLine | null;
}

/**
 * What Project Signal did or will do with this card. Sent lines read the value recorded when the
 * uploader stamped the card; unsent lines price it at today's close rate, so they move until sent.
 * `card` may carry unsaved drawer edits (deal value) so the preview follows the form.
 */
export const adsReadout = (card: Prospect, closeRate: number | null): AdsReadout => {
  const kind = adsClickKind(card);
  const email = card.contact_email?.trim();
  const match = kind ? `Matched by ${kind}` : email ? 'Matched by hashed email (no click id)' : 'No click id or email to match on';
  const matchTone = kind || email ? 'ok' : 'weak';
  const base = { error: card.ads_upload_error ?? null, match, matchTone } as const;

  if (card.created_at && new Date(card.created_at) < ADS_UPLOAD_START) {
    return { ...base, blocked: `Created before ${shortDate(ADS_UPLOAD_START.toISOString())}, when uploads began, so nothing is sent.`, qualified: null, closed: null };
  }

  const deal = Number(card.deal_value) || 0;
  const lostBeforeQualified = card.stage === 'closed_lost' && !card.qualified_at;

  let qualified: AdsLine;
  if (card.ads_qualified_uploaded_at) {
    const rate = card.ads_qualified_close_rate == null ? null : Number(card.ads_qualified_close_rate);
    const sent = card.ads_qualified_value == null ? null : Number(card.ads_qualified_value);
    // The deal value at upload is sent ÷ rate; today's deal_value may have been edited since.
    const dealAtUpload = rate && sent != null ? `${formatMoney(Math.round(sent / rate))} deal × ` : 'deal × ';
    qualified = {
      state: 'sent',
      status: `Sent ${shortDate(card.ads_qualified_uploaded_at)}`,
      value: sent,
      basis: rate != null && sent != null ? `${dealAtUpload}${formatRate(rate)} close rate` : 'value not recorded',
    };
  } else {
    const value = closeRate != null ? qualifiedLeadValue(deal, closeRate) : null;
    const basis = `${formatMoney(deal)} deal × ${formatRate(closeRate)} close rate`;
    qualified = lostBeforeQualified
      ? { state: 'never', status: "Won't send", value: null, basis: 'Closed lost without reaching Qualified' }
      : card.qualified_at
        ? { state: 'queued', status: 'Next upload', value, basis }
        : { state: 'later', status: 'At Qualified', value, basis };
  }

  let closed: AdsLine;
  const closeValue = closedSaleValue({ gross_profit: card.gross_profit, deal_value: deal });
  const closeBasis = card.gross_profit != null ? 'gross profit' : 'deal value (no gross profit entered)';
  if (card.ads_closed_uploaded_at) {
    closed = {
      state: 'sent',
      status: `Sent ${shortDate(card.ads_closed_uploaded_at)}`,
      value: card.ads_closed_value == null ? null : Number(card.ads_closed_value),
      basis: card.ads_closed_value != null ? closeBasis : 'value not recorded',
    };
  } else if (card.stage === 'closed_lost') {
    closed = { state: 'never', status: "Won't send", value: null, basis: 'Closed lost' };
  } else if (card.closed_won_at) {
    closed = { state: 'queued', status: 'Next upload', value: closeValue, basis: closeBasis };
  } else {
    closed = { state: 'later', status: 'At Closed Won', value: closeValue, basis: closeBasis };
  }

  return { ...base, blocked: null, qualified, closed };
};

/** Where a lead first came from (prospects.traffic_source, set by bullfinch.io from its first-visit cookie). */
export const TRAFFIC_SOURCE_LABEL: Record<string, string> = {
  google_ads: 'Google Ads',
  google_maps: 'Google Maps',
  google_search: 'Google search',
  other_search: 'Other search',
  ai_assistant: 'AI assistant',
  social: 'Social',
  campaign: 'Campaign link',
  referral: 'Referral',
  direct: 'Direct',
};

export interface LeadOrigin {
  /** "Google search · /denver-web-design-company/" */
  line: string;
  /** "First visit Sep 28 · +12d" (days until they became a lead), or null when the first visit isn't known */
  visit: string | null;
  /** Hover: the referring site or utm tags, and the full dates. */
  title: string;
}

/** The card's origin line, or null for leads from before attribution existed (2026-10-10). */
export const leadOrigin = (
  p: Pick<Prospect, 'traffic_source' | 'landing_page' | 'first_visit_at' | 'referrer' | 'created_at'>,
): LeadOrigin | null => {
  if (!p.traffic_source && !p.landing_page && !p.first_visit_at) return null;
  const source = p.traffic_source ? (TRAFFIC_SOURCE_LABEL[p.traffic_source] ?? p.traffic_source) : null;
  const label = source === 'Referral' && p.referrer ? p.referrer.replace(/^www\./, '') : source;
  const days =
    p.first_visit_at && p.created_at
      ? Math.max(0, Math.round((Date.parse(p.created_at) - Date.parse(p.first_visit_at)) / 86_400_000))
      : null;
  const parts = [label, p.landing_page].filter(Boolean);
  const visit = p.first_visit_at
    ? `First visit ${shortDate(p.first_visit_at)}${days != null ? ` · ${days === 0 ? 'same day' : `+${days}d`}` : ''}`
    : null;
  const title = [
    source ? `Source: ${source}${p.referrer ? ` (${p.referrer})` : ''}` : null,
    p.landing_page ? `Landed on ${p.landing_page}` : null,
    p.first_visit_at ? `First visit ${new Date(p.first_visit_at).toLocaleString('en-US', { timeZone: 'America/Denver' })}` : null,
    p.created_at ? `Became a lead ${new Date(p.created_at).toLocaleString('en-US', { timeZone: 'America/Denver' })}` : null,
  ]
    .filter(Boolean)
    .join('\n');
  return { line: parts.join(' · '), visit, title };
};
